import { Box, CircularProgress, Divider, SelectChangeEvent, Typography } from '@mui/material';
import CustomButton from '@src/components/CustomButton/CustomButton';
import CustomDatePicker from '@src/components/CustomDatePicker/CustomDatePicker';
import CustomModal from '@src/components/CustomModal';
import LabeledSelectField from '@src/components/CustomSelect/LabeledSelectField';
import LabeledTextField from '@src/components/CustomTextField/LabeledTextField';
import { InfoCircleIcon, ModalCloseIcon } from '@src/components/Icons';
import ImageThumnail from '@src/components/ImagePreview/ImageThumnail';
import DragDrop from '@src/components/ImageUploader/DragDrop';
import { IsMobileWidth } from '@src/components/MediaQueryUtils';
import { PaymentData } from '@src/components/OrderView/ViewOrder';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import {
	cleanObject,
	convertUTCDateIntoIndianTimezone,
	generateUniqueID,
} from '@src/helpers/Helpers';
import { Image } from '@src/interfaces/type';
import { AxiosError, AxiosResponse } from 'axios';
import moment from 'moment';
import React, { useState } from 'react';
import styled from 'styled-components';

const Container = styled.div`
	display: flex;
	flex-direction: column;
`;

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	letter-spacing: 0.28px;
	width: 100%;
	padding: 16px;
	gap: 14px;
`;

const StyledLabel = styled(Box)`
	color: var(--color-font-grey);
	font-size: 14px;
	font-weight: 600;
	line-height: 15.6px;
	span {
		color: #f26184;
		margin-left: 4px;
	}
`;

const InfoMessage = styled.div`
	font-size: 12px;
	font-weight: 500;
	flex-shrink: 0;
	color: #5c5c5c;
	.bold {
		font-weight: 700;
		color: #1a1d1f;
	}
`;

export interface StatusOption {
	color: string;
	color_bg: string;
	name: string;
	id: number;
	validation_order: number;
	group_id: number;
}

type RequiredDoc = 'lrcopy' | 'ewaybill';

interface Props {
	selectedOption: StatusOption;
	open: boolean;
	setOpen: (data: boolean) => void;
	statusUpdate: (
		selectedValue: number,
		selectedOption: StatusOption,
		data: any,
		data2: any
	) => void;
	loading: boolean;
	paymentOptions: PaymentData | null;
	orderDate?: any;
	total: number;
}

const OrderStatusExtraInfo: React.FC<Props> = ({
	selectedOption,
	open,
	setOpen,
	statusUpdate,
	loading,
	paymentOptions,
	orderDate,
	total,
}) => {
	const { apiService }: any = React.useContext(ApplicationContext);
	const isMobileWidth = IsMobileWidth();
	const [files, setFiles] = useState<File[]>([]);
	const [lrFiles, setLRFiles] = useState<File[]>([]);
	const [eWayBillFiles, seEtWayBillLRFiles] = useState<File[]>([]);
	const [images, setImages] = useState<Image[]>([]);
	const [lrCopyImages, setLrCopyImages] = useState<Image[]>([]);
	const [ewayBillImages, setEwauyBillImages] = useState<Image[]>([]);
	const [salesOrderData, setSalesOrderData] = useState({
		salesOrder: '',
		packingInstructions: '',
		logisticInstructions: '',
		remarks: '',
		customerPO: '',
		paymentTerms: '',
	});

	const [invoiceData, setInvoiceData] = useState({
		invoice: '',
		LRCopy: '',
		eWayBill: '',
	});

	const [paymentData, setPaymentData] = useState({
		receivedAmount: '',
		paymentStatus: '',
		paymentMode: '',
	});

	const [sodDate, setSODate] = useState<Date | null>(null);

	const isRequiredDocument: RequiredDoc = total < 50000 ? 'lrcopy' : 'ewaybill';

	const orderFulfillmentLifeCycle = {
		accepted: {
			statusId: 2,
			statusGroup: {
				id: 2,
				name: 'Confirmation',
			},
			salesOrderNumber: salesOrderData?.salesOrder,
			packingInstructions: salesOrderData?.packingInstructions,
			logisticInstructions: salesOrderData?.logisticInstructions,
			paymentTerms: {
				id: salesOrderData?.paymentTerms,
				name: paymentOptions?.paymentTerms?.find(
					(item) => item?.id === salesOrderData?.paymentTerms
				)?.value,
			},
			remarks: salesOrderData?.remarks,
			soDate: moment(sodDate).format('DD-MMMM-YYYY'),
			customerPoNumber: salesOrderData?.customerPO,
			attachments: {
				salesOrder: null,
			},
		},
		readyToShip: {
			statusId: 4,
			statusGroup: {
				id: 3,
				name: 'Processing and Shipping',
			},
			invoiceNumber: invoiceData?.invoice,
			attachments: {
				lrCopy: lrCopyImages?.[0]?.url,
				eWayBill: ewayBillImages?.[0]?.url,
			},
		},
		shipped: {
			statusId: 5,
			statusGroup: {
				id: 4,
				name: 'Delivery',
			},
			paymentMode: {
				id: paymentData?.paymentMode,
				name: paymentOptions?.paymentModes?.find((item) => item?.id === paymentData?.paymentMode)
					?.value,
			},
			paymentStatus: {
				id: paymentData?.paymentStatus,
				name: paymentOptions?.paymentStatuses?.find(
					(item) => item?.id === paymentData?.paymentStatus
				)?.value,
			},
			receivedAmount: paymentData?.receivedAmount,
			attachments: {
				paymentAttachment: images?.[0]?.url,
			},
		},
	};

	const disableAcceptedBtn = () => {
		const {
			salesOrder,
			packingInstructions,
			logisticInstructions,
			remarks,
			customerPO,
			paymentTerms,
		} = salesOrderData;
		if (!salesOrder || !packingInstructions || !sodDate || !logisticInstructions || !paymentTerms) {
			return true;
		}
		return false;
	};

	const disableReadyToShippedBtn = () => {
		if (isRequiredDocument === 'lrcopy' && !lrCopyImages?.length) {
			return true;
		} else if (isRequiredDocument === 'ewaybill' && !ewayBillImages?.length) {
			return true;
		}
		return false;
	};

	const uploadImage = (file: any) => {
		return new Promise((resolve, reject) => {
			let url = '';
			const formData = new FormData();
			formData.append('file', file);

			apiService?.uploadProductImage(
				formData,
				(response: AxiosResponse) => {
					url = response?.data?.data?.url;
					resolve(url);
				},
				(error: AxiosError) => {
					reject(error);
				}
			);
		});
	};

	const handleFileChange = async (
		event: React.ChangeEvent<HTMLInputElement>,
		setFiles: any,
		setImages: any,
		maxLength: number = 1
	) => {
		event.preventDefault();
		const filesData = event?.target?.files;
		let imageCount = images?.length + (filesData?.length || 0);
		let validFiles = [];
		let imagesData = [...images];

		let files = [];

		if (imageCount <= maxLength) {
			if (filesData) {
				for (let i = 0; i < filesData.length; i++) {
					const file = filesData[i];

					if (file.type === 'application/pdf') {
						const formData = new FormData();
						formData.append('file', file);

						//pdf upload call
						return;
					}

					validFiles.push(file);
					const fileURL = await uploadImage(file);
					imagesData.push({ id: generateUniqueID(), url: fileURL as string, hover: false });
					files?.push(file);
				}
			}

			setFiles([...files]);
			setImages(imagesData);
		} else {
		}
	};

	const handleFileDrop = async (
		event: React.DragEvent<HTMLDivElement>,
		setFiles: any,
		setImages: any,
		maxLength: number = 1
	) => {
		event.preventDefault();
		const filesData = event.dataTransfer?.files;
		let imageCount = images?.length + (filesData?.length || 0);
		let validFiles = [];
		let imagesData = [...images];

		let files = [];

		if (imageCount <= maxLength) {
			if (filesData) {
				for (let i = 0; i < filesData.length; i++) {
					const file = filesData[i];
					if (file.type === 'application/pdf') {
						const formData = new FormData();
						formData.append('file', file);

						//pdf upload call
						return;
					}
					validFiles.push(file);
					const fileURL = await uploadImage(file);
					imagesData.push({ id: generateUniqueID(), url: fileURL as string, hover: false });
					files?.push(file);
				}
			}
			setFiles([...files]);
			setImages(imagesData);
		} else {
		}
	};

	const renderContent = () => {
		if (Number(selectedOption?.id) === 2) {
			return (
				<StyledContainer>
					<div style={{ width: '100%' }}>
						<LabeledTextField
							required
							label="Sales Order"
							value={salesOrderData?.salesOrder}
							onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
								setSalesOrderData({
									...salesOrderData,
									salesOrder: e?.target?.value as string,
								})
							}
							placeholder="eg. SO12345"
							styles={{ width: '100%', height: '36px' }}
						/>
					</div>
					<div style={{ width: '100%' }}>
						<LabeledTextField
							required
							label="Packing Instructions"
							value={salesOrderData?.packingInstructions}
							onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
								setSalesOrderData({
									...salesOrderData,
									packingInstructions: e?.target?.value as string,
								})
							}
							placeholder="eg. Handle with care, fragile items."
							styles={{ width: '100%', height: '36px' }}
						/>
					</div>
					<div style={{ width: '100%' }}>
						<LabeledTextField
							required
							label="Logistic Instructions"
							value={salesOrderData?.logisticInstructions}
							onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
								setSalesOrderData({
									...salesOrderData,
									logisticInstructions: e?.target?.value as string,
								})
							}
							placeholder="eg. Deliver between 9 AM to 5 PM."
							styles={{ width: '100%', height: '36px' }}
						/>
					</div>

					<div style={{ width: '100%' }}>
						<LabeledSelectField
							required
							label="Payment Terms"
							value={salesOrderData?.paymentTerms}
							options={paymentOptions?.paymentTerms || []}
							handleChange={(e: SelectChangeEvent<unknown>) =>
								setSalesOrderData({
									...salesOrderData,
									paymentTerms: e?.target?.value as string,
								})
							}
							placeholder="eg. Immediate 100%"
							styles={{ width: '100%', height: '36px' }}
							disablePortal={false}
						/>
					</div>
					<div style={{ width: '100%' }}>
						<LabeledTextField
							label="Remarks"
							value={salesOrderData?.remarks}
							onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
								setSalesOrderData({
									...salesOrderData,
									remarks: e?.target?.value as string,
								})
							}
							placeholder="eg. Urgent order, prioritize delivery."
							styles={{ width: '100%', height: '36px' }}
						/>
					</div>
					<div style={{ width: '100%' }}>
						<LabeledTextField
							label="Customer PO"
							value={salesOrderData?.customerPO}
							onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
								setSalesOrderData({
									...salesOrderData,
									customerPO: e?.target?.value as string,
								})
							}
							placeholder="eg. PO98765"
							styles={{ width: '100%', height: '36px' }}
						/>
					</div>
					<div style={{ width: '100%' }}>
						<CustomDatePicker
							label="SO Date"
							styles={{ width: '100%' }}
							selectedDate={sodDate}
							onDateChange={(newDate) => {
								//Getting date in UTC, adding minutes according to indian timezone diff
								setSODate(convertUTCDateIntoIndianTimezone(newDate as Date));
							}}
							minDate={moment(orderDate, 'MMM DD, YYYY hh:mma').toDate()}
						/>
					</div>
					<div
						style={{
							display: 'flex',
							alignItems: 'flex-end',
							justifyContent: 'flex-end',
							width: '100%',
						}}
					>
						<CustomButton
							title={
								loading ? (
									<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
								) : (
									'Accepted'
								)
							}
							handleClick={() =>
								statusUpdate(
									selectedOption?.id,
									selectedOption,
									cleanObject({
										statusId: selectedOption?.id,
										salesOrderNumber: salesOrderData?.salesOrder,
										packingInstructions: salesOrderData?.packingInstructions,
										logisticInstructions: salesOrderData?.logisticInstructions,
										paymentTermsId: salesOrderData?.paymentTerms,
										remarks: salesOrderData?.remarks,
										salesOrderDate: sodDate,
										customerPoNumber: salesOrderData?.customerPO,
										// salesOrderImage: null,
										// shippingInvoiceNumber: null,
										// lrCopyImage: null,
										// eWayBillImage: null,
										// shippingImages: [],
										// paymentModeId: null,
										// receivedAmount: null,
										// paymentStatusId: null,
										// paymentAttachment: null,
										// rejectedQuantity: null,
										// postDeliveryIssue: null,
										// postDeliveryImages: [],
										// creditNoteNumber: null,
										// creditNoteImage: null,
										// returnInvoiceNumber: null,
										// returnIssue: null,
										// returnImages: [],
									}),
									orderFulfillmentLifeCycle
								)
							}
							heightVariant="medium"
							color="primary"
							styles={{
								width: '160px',
								height: '36px',
								padding: '7px 12px',
							}}
							disabled={disableAcceptedBtn() || loading}
						/>
					</div>
				</StyledContainer>
			);
		} else if (Number(selectedOption?.id) === 4) {
			return (
				<StyledContainer>
					<div style={{ width: '100%' }}>
						<LabeledTextField
							required
							label="Invoice"
							value={invoiceData?.invoice}
							onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
								setInvoiceData({
									...invoiceData,
									invoice: e?.target?.value as string,
								})
							}
							placeholder="eg. INV45678"
							styles={{ width: '100%', height: '36px' }}
						/>
					</div>
					<div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
						<StyledLabel>
							LR Copy <span>{isRequiredDocument === 'lrcopy' ? '*' : ''}</span>
						</StyledLabel>
						<div style={{ width: '100%' }}>
							{lrCopyImages?.length ? (
								<ImageThumnail
									images={lrCopyImages}
									setImages={setLrCopyImages}
									setFiles={setLRFiles}
									style={{ width: '100%' }}
									handleFileChange={(e) => handleFileChange(e, setLRFiles, setLrCopyImages)}
									maxLength={1}
								/>
							) : (
								<DragDrop
									handleFileChange={(e) => handleFileChange(e, setLRFiles, setLrCopyImages)}
									handleFileDrop={(e) => handleFileDrop(e, setLRFiles, setLrCopyImages)}
									style={{ width: '100%' }}
									text="Capture / select image to upload"
									info="Image in png or jpg format."
								/>
							)}
						</div>
					</div>
					<div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
						<StyledLabel>
							E-way Bill <span>{isRequiredDocument === 'ewaybill' ? '*' : ''}</span>
						</StyledLabel>
						<div style={{ width: '100%' }}>
							{ewayBillImages?.length ? (
								<ImageThumnail
									images={ewayBillImages}
									setImages={setEwauyBillImages}
									setFiles={seEtWayBillLRFiles}
									style={{ width: '100%' }}
									handleFileChange={(e) =>
										handleFileChange(e, seEtWayBillLRFiles, setEwauyBillImages)
									}
									maxLength={1}
								/>
							) : (
								<DragDrop
									handleFileChange={(e) =>
										handleFileChange(e, seEtWayBillLRFiles, setEwauyBillImages)
									}
									handleFileDrop={(e) => handleFileDrop(e, seEtWayBillLRFiles, setEwauyBillImages)}
									style={{ width: '100%' }}
									text="Capture / select image to upload"
									info="Image in png or jpg format."
								/>
							)}
						</div>
					</div>
					<InfoMessage>
						<InfoCircleIcon fill="#FFBE00" /> If the total is over 50k, you need to upload an{' '}
						<span className="bold">E-way bill</span>; otherwise, just an{' '}
						<span className="bold">LR copy</span>.
					</InfoMessage>

					<div
						style={{
							display: 'flex',
							alignItems: 'flex-end',
							justifyContent: 'flex-end',
							width: '100%',
						}}
					>
						<CustomButton
							title={
								loading ? (
									<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
								) : (
									'Ready To Ship'
								)
							}
							handleClick={() =>
								statusUpdate(
									selectedOption?.id,
									selectedOption,
									cleanObject({
										statusId: selectedOption?.id,
										shippingInvoiceNumber: invoiceData?.invoice,
										lrCopyImage: lrCopyImages?.[0]?.url,
										eWayBillImage: ewayBillImages?.[0]?.url,
									}),
									orderFulfillmentLifeCycle
								)
							}
							heightVariant="medium"
							color="primary"
							styles={{
								width: '160px',
								height: '36px',
								padding: '7px 12px',
							}}
							disabled={!invoiceData?.invoice || loading || disableReadyToShippedBtn()}
						/>
					</div>
				</StyledContainer>
			);
		} else if (Number(selectedOption?.id) === 5) {
			return (
				<StyledContainer>
					<div style={{ width: '100%' }}>
						<LabeledSelectField
							required
							label="Payment Mode"
							value={paymentData?.paymentMode}
							options={paymentOptions?.paymentModes || []}
							handleChange={(e: SelectChangeEvent<unknown>) =>
								setPaymentData({
									...paymentData,
									paymentMode: e?.target?.value as string,
								})
							}
							placeholder="eg. Immediate 100%"
							styles={{ width: '100%', height: '36px' }}
							disablePortal={false}
						/>
					</div>
					<div style={{ width: '100%' }}>
						<LabeledTextField
							required
							label="Received Amount"
							value={paymentData?.receivedAmount}
							onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
								const value = e?.target?.value;
								// Regex to allow only positive numbers (including zero)
								const regex = /^[+]?\d*$/;

								if (regex.test(value)) {
									setPaymentData({
										...paymentData,
										receivedAmount: value,
									});
								}
							}}
							placeholder="eg. 5000"
							styles={{ width: '100%', height: '36px' }}
						/>
					</div>
					<div style={{ width: '100%' }}>
						<LabeledSelectField
							required
							label="Payment Status"
							value={paymentData?.paymentStatus}
							options={paymentOptions?.paymentStatuses || []}
							handleChange={(e: SelectChangeEvent<unknown>) =>
								setPaymentData({
									...paymentData,
									paymentStatus: e?.target?.value as string,
								})
							}
							placeholder="eg. Unpaid"
							styles={{ width: '100%', height: '36px' }}
							disablePortal={false}
						/>
					</div>
					<div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
						<StyledLabel>Payment Attachment</StyledLabel>
						<div style={{ width: '100%' }}>
							{images?.length ? (
								<ImageThumnail
									images={images}
									setImages={setImages}
									setFiles={setFiles}
									style={{ width: '100%' }}
									handleFileChange={(e) => handleFileChange(e, setFiles, setImages)}
									accept="image/png, image/jpeg, image/gif, application/pdf"
									maxLength={3}
								/>
							) : (
								<DragDrop
									handleFileChange={(e) => handleFileChange(e, setFiles, setImages)}
									handleFileDrop={(e) => handleFileDrop(e, setFiles, setImages)}
									style={{ width: '100%' }}
									text="Capture / select image to upload"
									info="Image in png or jpg format."
									accept="image/png, image/jpeg, image/gif, application/pdf"
									multiple
								/>
							)}
						</div>
					</div>
					<div
						style={{
							display: 'flex',
							alignItems: 'flex-end',
							justifyContent: 'flex-end',
							width: '100%',
						}}
					>
						<CustomButton
							title={
								loading ? (
									<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
								) : (
									'Shipped'
								)
							}
							handleClick={() =>
								statusUpdate(
									selectedOption?.id,
									selectedOption,
									cleanObject({
										statusId: selectedOption?.id,
										paymentModeId: paymentData?.paymentMode,
										receivedAmount: paymentData?.receivedAmount,
										paymentStatusId: paymentData?.paymentStatus,
										paymentAttachment: images?.[0]?.url,
									}),
									orderFulfillmentLifeCycle
								)
							}
							heightVariant="medium"
							color="primary"
							styles={{
								width: '160px',
								height: '36px',
								padding: '7px 12px',
							}}
							disabled={
								!paymentData?.paymentMode ||
								!paymentData?.paymentStatus ||
								!paymentData?.receivedAmount ||
								loading
							}
						/>
					</div>
				</StyledContainer>
			);
		}

		return null;
	};

	return (
		<CustomModal
			open={open}
			setOpen={setOpen}
			header={false}
			style={
				isMobileWidth
					? { width: '100%', minHeight: '100vh' }
					: {
							maxWidth: '472px',
							minWidth: '472px',
							maxHeight: 'auto',
					  }
			}
		>
			<Container>
				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					px={2}
					width="100%"
					height="56px"
				>
					<Typography variant="body1">
						{Number(selectedOption?.id) === 2 && 'Sales Order'}
						{Number(selectedOption?.id) === 4 && 'Invoice, LR Copy, E-way Bill'}
						{Number(selectedOption?.id) === 5 && 'Payment Attachment'}
					</Typography>
					<Box sx={{ cursor: 'pointer' }} onClick={() => setOpen(false)}>
						<ModalCloseIcon />
					</Box>
				</Box>
				<Divider />
				{renderContent()}
			</Container>
		</CustomModal>
	);
};

export default OrderStatusExtraInfo;
